import React, { useState, useEffect } from "react";

interface EmailInputProps {
  value: string;
  onChange: (emails: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  onFocus?: () => void;
  required?: boolean;
  label?: string;
  name?: string;
  id?: string;
  containerClassName?: string;
  secondaryContainerClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
}

const EmailInput: React.FC<EmailInputProps> = ({
  value,
  onChange,
  placeholder = "Enter email addresses",
  disabled = false,
  error,
  onFocus,
  required = false,
  label,
  name,
  id,
  containerClassName = "",
  secondaryContainerClassName = "",
  inputClassName = "",
  labelClassName = "",
  errorClassName = "",
}) => {
  const [inputValue, setInputValue] = useState("");
  const inputId =
    id || name || `email-input-${Math.random().toString(36).slice(2, 11)}`;
  const errorId = `${inputId}-error`;

  useEffect(() => {
    const currentEmails = value
      ? value
          .split(/[,\s]+/)
          .map((email) => email.trim())
          .filter((email) => email.length > 0)
      : [];

    if (currentEmails.length === 0) {
      setInputValue("");
    }
  }, [value]);

  const processEmailInput = (input: string): string[] => {
    if (!input.trim()) return [];
    return input
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange(processEmailInput(e.target.value));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && inputValue === "") {
      const existingEmails = value
        ? value
            .split(/[,\s]+/)
            .map((email) => email.trim())
            .filter((email) => email.length > 0)
        : [];

      if (existingEmails.length > 0) {
        const updatedEmails = existingEmails.slice(0, -1);
        onChange(updatedEmails);
      }
    }
  };

  return (
    <div className={`flex flex-col ${containerClassName} relative`}>
      {label && (
        <label htmlFor={inputId} className={`${labelClassName}`}>
          {label}
          {required && <span>*</span>}
        </label>
      )}

      <div className={`flex items-center ${secondaryContainerClassName}`}>
        <input
          id={inputId}
          name={name}
          type="text"
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error ? errorId : undefined}
          className={`w-full bg-transparent outline-none flex-1 border-none font-normal text-md text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder placeholder:font-normal placeholder:text-md ${inputClassName}`}
        />
      </div>

      {error && (
        <div
          id={errorId}
          role="alert"
          className={`${errorClassName} absolute top-full ml-0.5`}
        >
          <span className="text-verba-red-alert dark:text-verba-red-alert-dk text-xs font-normal">
            {error}
          </span>
        </div>
      )}
    </div>
  );
};

export default EmailInput;
